import { useEffect, useRef, useState } from 'react';
import { useInterval } from 'usehooks-ts';

export interface IOscillation {
	period: number;
	value: number;
}

interface IOscillator {
	oscillations: IOscillation[];
	shouldFire?: boolean;
}

export const generateCubicDistribution = (floor: number, ceiling: number, bins: number): number[] => {
	const binIndices = Array.from(Array(bins).keys());

	const isIncreasing = floor < ceiling;
	const slopeDeterminant = isIncreasing ? 1 : -1;
	const delta = Math.abs(ceiling - floor);

	const rangeEnd /** 180deg */ = Math.PI;
	const rangeDelta = Math.abs(rangeEnd);

	/** Non-negative, shifted sine wave */
	const curve = (at: number) => (Math.sin(at - Math.PI / 2) + 1) / 2;

	const distributionPoints = binIndices.map(index => curve((index + 1) * (rangeDelta / bins)));
	distributionPoints.unshift(curve(0));

	return distributionPoints.map(point => floor + slopeDeterminant * delta * point);
};

/**
 * @TODO: [9 hz / 1/3 s] start damping
 */
const useOscillator = ({ oscillations, shouldFire = true }: IOscillator) => {
	const [period, setPeriod] = useState<number | null>(null);
	const [value, setValue] = useState(0);

	const iterator = useRef(oscillations.length === 1 ? 0 : 1);

	useEffect(() => {
		if (!shouldFire) {
			return;
		}

		setPeriod(oscillations[0].period);
		setValue(oscillations[0].value);
	}, [shouldFire]);

	useInterval(
		() => {
			const oscillation = oscillations[iterator.current];

			setPeriod(oscillation.period);
			setValue(oscillation.value);

			if (iterator.current + 1 < oscillations.length) {
				++iterator.current;
			} else {
				iterator.current = 0;
			}
		},
		shouldFire ? period : null
	);

	return { value };
};

export default useOscillator;
